import React from "react";
import { Check } from "lucide-react";
import { SessionSchedulingType } from "src/services/sessionsService";

interface TutorAvailabilityRadarProps {
  tutorName: string;
  tutorTimezone: string;
  studentName: string;
  duration: string;
  date: string;
  startTime: string;
  onSelectSlot: (date: string, time: string) => void;
  schedulingType: SessionSchedulingType;
  weekdays: number[];
  onToggleWeekday?: (day: number) => void;
  projectedDatesCount: number;
  calculatedSeriesEndDate: string;
  startDate: string;
}

export const TutorAvailabilityRadar: React.FC<TutorAvailabilityRadarProps> = ({
  tutorName,
  tutorTimezone,
  studentName,
  duration,
  date,
  startTime,
  onSelectSlot,
  schedulingType,
  weekdays,
  onToggleWeekday,
  projectedDatesCount,
  calculatedSeriesEndDate,
  startDate,
}) => {
  return (
    <div className="border border-slate-200 rounded-xl p-4 md:p-5 bg-white space-y-4 shadow-xs">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="font-bold text-xs md:text-sm text-slate-900">
            {tutorName} Availability Radar
          </span>
          <span className="text-xs font-mono text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded">
            {tutorTimezone}
          </span>
        </div>
        <div className="text-xs text-slate-500 font-mono">
          Showing 09:00 - 18:00 GMT (14:30 - 23:30 IST)
        </div>
      </div>

      {/* 5-DAY INTERACTIVE SCHEDULE MATRIX */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 text-xs">
        {/* Mon 2 */}
        <div className="space-y-2">
          <div className="text-center font-bold text-slate-800 pb-1 border-b border-slate-200 flex items-center justify-center gap-1">
            <span>Mon 2</span>
            {schedulingType === "recurring" && weekdays.includes(1) && (
              <span className="text-xs font-mono text-purple-600 font-bold">● Recur</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => onSelectSlot("2026-11-02", "14:30")}
            className="w-full p-2 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 text-center border border-slate-200 transition"
          >
            09:00 - 11:00 Free
          </button>
          
          {/* Active Selected Target Slot */}
          <div className="p-2.5 rounded-lg bg-purple-100/90 border-2 border-purple-600 text-purple-900 font-bold text-center shadow-xs">
            <div className="flex items-center justify-center gap-1">
              <span>★ 11:00 AM (Target Slot)</span>
            </div>
            <div className="text-xs font-normal text-purple-700 mt-0.5">
              {studentName} • {duration} min
            </div>
            <div className="text-xs font-mono text-purple-800 font-semibold mt-0.5">
              {startTime} Local IST
            </div>
          </div>

          <button
            type="button"
            onClick={() => onSelectSlot("2026-11-02", "17:30")}
            className="w-full p-2 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 text-center border border-slate-200 transition"
          >
            12:00 - 14:00 Free
          </button>
          <div className="p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-center text-xs cursor-not-allowed">
            14:00 Busy (Booked)
          </div>
        </div>

        {/* Tue 3 */}
        <div className="space-y-2">
          <div className="text-center font-bold text-slate-800 pb-1 border-b border-slate-200">Tue 3</div>
          <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-center text-xs font-medium cursor-not-allowed">
            ✕ Tutor Unavailable
            <div className="text-xs text-rose-600 mt-1">Research Block</div>
          </div>
        </div>

        {/* Wed 4 */}
        <div className="space-y-2">
          <div className="text-center font-bold text-slate-800 pb-1 border-b border-slate-200 flex items-center justify-center gap-1">
            <span>Wed 4</span>
            {schedulingType === "recurring" && weekdays.includes(3) && (
              <span className="text-xs font-mono text-purple-600 font-bold">● Recur</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => onSelectSlot("2026-11-04", "14:30")}
            className="w-full p-2 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 text-center border border-slate-200 transition"
          >
            09:00 - 11:00 Free
          </button>
          <button
            type="button"
            onClick={() => onSelectSlot("2026-11-04", "16:30")}
            className="w-full p-2.5 rounded-lg border-2 border-purple-600 bg-purple-50 text-purple-900 font-bold text-center shadow-xs"
          >
            ★ 11:00 AM Free
            <div className="text-xs font-normal text-purple-700">16:30 Local IST</div>
          </button>
          <button
            type="button"
            onClick={() => onSelectSlot("2026-11-04", "17:30")}
            className="w-full p-2 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 text-center border border-slate-200 transition"
          >
            12:00 - 18:00 Free
          </button>
        </div>

        {/* Thu 5 */}
        <div className="space-y-2">
          <div className="text-center font-bold text-slate-800 pb-1 border-b border-slate-200">Thu 5</div>
          <button
            type="button"
            onClick={() => {
              if (schedulingType === "recurring" && onToggleWeekday) {
                onToggleWeekday(4);
              } else {
                onSelectSlot("2026-11-05", "15:30");
              }
            }}
            className="w-full p-4 rounded-lg bg-slate-50 hover:bg-purple-50 hover:border-purple-300 text-slate-600 text-center border border-slate-200 transition"
          >
            <div className="font-semibold text-slate-800">
              {schedulingType === "recurring" ? "+ Add Thursday" : "All Day Open"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Click to allocate slot</div>
          </button>
        </div>

        {/* Fri 6 */}
        <div className="space-y-2">
          <div className="text-center font-bold text-slate-800 pb-1 border-b border-slate-200">Fri 6</div>
          <div className="p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-center text-xs cursor-not-allowed">
            10:00 Group Session
            <div className="text-xs text-amber-700">Collision</div>
          </div>
          <button
            type="button"
            onClick={() => onSelectSlot("2026-11-06", "17:30")}
            className="w-full p-2 rounded bg-slate-50 hover:bg-slate-100 text-slate-600 text-center border border-slate-200 transition"
          >
            12:00 - 16:00 Free
          </button>
        </div>
      </div>

      {/* COLLISION-FREE STATUS BANNER */}
      <div className="p-3.5 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2.5 text-xs text-emerald-900">
        <div className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
          <Check className="w-3.5 h-3.5" />
        </div>
        <div>
          <strong>
            {schedulingType === "recurring" ? "4-Week Series Conflict Scan Passed:" : "Collision-Free Greenlight:"}
          </strong>{" "}
          {schedulingType === "recurring"
            ? `Verified all ${projectedDatesCount} occurrences (${startDate} to ${calculatedSeriesEndDate}) against master availability. 0 conflicts found.`
            : `${date} at ${startTime} has 0 overlaps with ${tutorName}'s calendar or student sessions.`}
        </div>
      </div>
    </div>
  );
};

